import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { DesiredAction } from '@dreamai/types';
import api from '../../../api';
import ProjectAppBlockRenderer, {
  CompletedAction,
} from './ProjectAppBlockRenderer';
import ShareProjectAppButton from './ShareProjectAppButton';
import DreamieAvatarButton from '../DreamieAvatarButton';
import AddTodoModal from '../sidebar/AddTodoModal';
import { useAgent } from '../../../contexts/AgentContext';
import { useMobileNav } from '../../../contexts/MobileNavContext';
import { ReactComponent as ChevronLeftIcon } from '../../../icons/chevron-left.svg';
import './research-block-modal.scss';

interface ResearchBlockModalProps {
  show: boolean;
  onClose: () => void;
  appId: string;
  title?: string;
  /** When provided, renders a DreamieAvatarButton to chat about the artifact */
  artifactId?: string;
  /** Agent ID for the DreamieAvatarButton */
  agentId?: string;
  agentImageUrl?: string;
  agentNickname?: string;
  /** Label for the back button (default: 'Back to Project Plan') */
  backLabel?: string;
  /** Suggested next-step actions to display in the hero header */
  desiredActions?: DesiredAction[];
  /** Parent thread ID for linking todos to the originating thread */
  parentThreadId?: string;
}

const ResearchBlockModal: React.FC<ResearchBlockModalProps> = ({
  show,
  onClose,
  appId,
  title,
  artifactId,
  agentId,
  agentImageUrl,
  agentNickname,
  backLabel = 'Back to Project Plan',
  desiredActions,
  parentThreadId,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { isMobile } = useMobileNav();
  const containerRef = useRef<HTMLElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showAddTodoModal, setShowAddTodoModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<DesiredAction | null>(
    null,
  );
  const [pendingActionSource, setPendingActionSource] = useState<
    'card' | 'suggestion' | null
  >(null);
  const [pendingBlockId, setPendingBlockId] = useState<string | undefined>(
    undefined,
  );
  const [completedActions, setCompletedActions] = useState<CompletedAction[]>(
    [],
  );
  const [dismissedSuggestionKeys, setDismissedSuggestionKeys] = useState<
    Set<string>
  >(new Set());
  const [fetchedDesiredActions, setFetchedDesiredActions] = useState<
    DesiredAction[] | undefined
  >(undefined);
  const [fetchedParentThreadId, setFetchedParentThreadId] = useState<
    string | undefined
  >(undefined);
  const { agents } = useAgent();
  const navigate = useNavigate();

  // Clear fetched actions when artifact changes or modal closes
  useEffect(() => {
    setFetchedDesiredActions(undefined);
  }, [artifactId, show]);

  // If desiredActions not provided as prop but we have an artifactId, fetch them
  useEffect(() => {
    if (desiredActions || !artifactId || !show) return;
    let cancelled = false;
    const fetchActions = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<{ success: boolean; artifact: any }>(
          `/response-artifacts/artifact/${artifactId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (
          !cancelled &&
          res.data.success &&
          res.data.artifact?.desiredActions
        ) {
          console.log(
            '[DEBUG_DA] ResearchBlockModal fetched desiredActions:',
            res.data.artifact.desiredActions,
          );
          setFetchedDesiredActions(res.data.artifact.desiredActions);
        }
        if (
          !cancelled &&
          res.data.success &&
          res.data.artifact?.parentThreadId
        ) {
          setFetchedParentThreadId(res.data.artifact.parentThreadId);
        }
      } catch (err) {
        console.error(
          '[DEBUG_DA] ResearchBlockModal failed to fetch desiredActions:',
          err,
        );
      }
    };
    fetchActions();
    return () => {
      cancelled = true;
    };
  }, [artifactId, desiredActions, show, getAccessTokenSilently]);

  // Resolve agent info for AddTodoModal
  const primaryAgent = agentId
    ? agents.find((a) => a.id === agentId)
    : agents[0];

  const handleSuggestionClick = useCallback((action: DesiredAction) => {
    setPendingAction(action);
    setPendingActionSource('suggestion');
    setShowAddTodoModal(true);
  }, []);

  const handleCardActionClick = useCallback(
    (action: DesiredAction, blockId: string) => {
      setPendingAction(action);
      setPendingActionSource('card');
      setPendingBlockId(blockId);
      setShowAddTodoModal(true);
    },
    [],
  );

  const handleAddTodoClose = useCallback(() => {
    setShowAddTodoModal(false);
    setPendingAction(null);
    setPendingActionSource(null);
    setPendingBlockId(undefined);
  }, []);

  const handleTodoCreated = useCallback(async () => {
    setShowAddTodoModal(false);
    const action = pendingAction;
    const source = pendingActionSource;
    const blockId = pendingBlockId;

    // Build the completed action ID
    const completedId =
      source === 'card' && blockId && action
        ? `block:${blockId}:${action.key}`
        : action
          ? `suggestion:${action.key}`
          : null;

    // Show "✓ Todo Created" animation
    if (completedId) {
      setCompletedActions((prev) => [
        ...prev,
        { id: completedId, phase: 'created' },
      ]);

      // After 1.5s, transition to "View in Todo List"
      setTimeout(() => {
        setCompletedActions((prev) =>
          prev.map((c) => (c.id === completedId ? { ...c, phase: 'view' } : c)),
        );
      }, 1500);

      // After 60s, remove the completed action entirely (suggestion-level only)
      if (source !== 'card' && action) {
        setTimeout(() => {
          setCompletedActions((prev) =>
            prev.filter((c) => c.id !== completedId),
          );
          setDismissedSuggestionKeys((prev) => new Set(prev).add(action.key));
        }, 60000);
      }
    }

    // Remove the desired action from the block in DB (card actions only)
    if (source === 'card' && blockId && action) {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(
          `/project-apps/blocks/${blockId}/actions/${action.key}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        console.error('Error removing action from block:', err);
      }
    }

    setPendingAction(null);
    setPendingActionSource(null);
    setPendingBlockId(undefined);
  }, [
    pendingAction,
    pendingActionSource,
    pendingBlockId,
    getAccessTokenSilently,
  ]);

  useEffect(() => {
    // Find the nearest .canvas-assets-content ancestor to portal into so the overlay
    // replaces the project app page area while keeping the tabs/header visible above.
    // Fall back to .canvas-assets, then body for fullscreen.
    const contentAncestor = anchorRef.current?.closest(
      '.canvas-assets-content',
    );
    const canvasAncestor =
      contentAncestor || anchorRef.current?.closest('.canvas-assets');
    const el = canvasAncestor || document.body;
    containerRef.current = el as HTMLElement;
    setIsFullscreen(!canvasAncestor);
    setMounted(true);
  }, []);

  if (!show || !mounted || !containerRef.current)
    return <div ref={anchorRef} style={{ display: 'none' }} />;

  return (
    <>
      <div ref={anchorRef} style={{ display: 'none' }} />
      {createPortal(
        <div
          className={`research-block-overlay${isFullscreen ? ' fullscreen' : ''}`}
        >
          <div className='research-block-overlay-header'>
            <button className='research-block-overlay-back' onClick={onClose}>
              <ChevronLeftIcon />
              <span>{backLabel}</span>
            </button>
            <ShareProjectAppButton appId={appId} />
          </div>
          <div className='research-block-overlay-body'>
            <ProjectAppBlockRenderer
              appId={appId}
              desiredActions={(desiredActions || fetchedDesiredActions)?.filter(
                (a) => !dismissedSuggestionKeys.has(a.key),
              )}
              onSuggestionClick={handleSuggestionClick}
              onCardActionClick={handleCardActionClick}
              agentName={primaryAgent?.nickname}
              completedActions={completedActions}
            />
            {artifactId && (
              <DreamieAvatarButton
                agentId={agentId}
                artifactId={artifactId}
                agentImageUrl={agentImageUrl}
                agentNickname={agentNickname}
                options={[
                  {
                    id: 'chat',
                    label: 'Chat about this',
                    builtInAction: 'chat',
                  },
                  {
                    id: 'add-todo',
                    label: 'Add to-do',
                    onSelect: () => setShowAddTodoModal(true),
                  },
                ]}
              />
            )}
          </div>
          {showAddTodoModal && isMobile && (
            <AddTodoModal
              isOpen={showAddTodoModal}
              onClose={handleAddTodoClose}
              agentName={primaryAgent?.nickname}
              agentImageUrl={primaryAgent?.image_url}
              agentId={primaryAgent?.id}
              onTodoCreated={handleTodoCreated}
              initialText={pendingAction?.todoText}
              initialAction={pendingAction || undefined}
              researchAppId={appId}
              defaultThreadId={parentThreadId || fetchedParentThreadId}
              artifactId={artifactId}
              isMobile
            />
          )}
        </div>,
        containerRef.current,
      )}
      {showAddTodoModal &&
        !isMobile &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1060,
              background: 'rgba(255, 254, 252, 0.6)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              justifyContent: 'center',
              paddingTop: '80px',
            }}
          >
            <AddTodoModal
              isOpen={showAddTodoModal}
              onClose={handleAddTodoClose}
              agentName={primaryAgent?.nickname}
              agentImageUrl={primaryAgent?.image_url}
              agentId={primaryAgent?.id}
              onTodoCreated={handleTodoCreated}
              initialText={pendingAction?.todoText}
              initialAction={pendingAction || undefined}
              researchAppId={appId}
              defaultThreadId={parentThreadId || fetchedParentThreadId}
              artifactId={artifactId}
            />
          </div>,
          document.body,
        )}
    </>
  );
};

export default ResearchBlockModal;
